import { StudentName } from "../../../shared-components/StudentName";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import Grid from "@mui/material/Grid";
import { FindClassAndAssignDialog } from "../components/FindClassAndAssignDialog";
import { StudentEditorDialog } from "../components/StudentEditorDialog";
import { InformationSnippet } from "../../../shared-components/InformationSnippet";
import { FruitifySchoolClassSearch } from "../../school/components/FruitifySchoolClassSearch";

export const StudentSummary = ({ student, onReload }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <StudentEditorDialog
        open={open}
        setOpen={setOpen}
        student={student}
        yearGroups={[student.yearGroup]} // TODO: add yearGroup options
        onReload={onReload}
      />
      <Card>
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h3">Student information</Typography>

            <Button
              variant={"outlined"}
              endIcon={<Edit />}
              onClick={() => setOpen(true)}
            >
              Edit
            </Button>
          </Stack>

          <Grid container spacing={2} direction={"row"} sx={{ mt: 2, mb: 2 }}>
            <Grid size={4}>
              <InformationSnippet
                title={"Name"}
                value={student.identifier}
                valueComponent={
                  <Typography
                    variant="subtitle1"
                    color="text.primary"
                    fontWeight={600}
                    gutterBottom
                  >
                    <StudentName student={student} />
                  </Typography>
                }
              />
            </Grid>
            <Grid size={4}>
              <InformationSnippet
                title={"Student Identifier"}
                value={student.studentIdentifier}
              />
            </Grid>
            <Grid size={4}>
              <InformationSnippet
                title={"School"}
                value={student.school.name}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

function StudentView() {
  const { id } = useParams();
  const ApiClient = useApiClient();
  const [possibleClasses, setPossibleClasses] = useState([]);
  const [student, setStudent] = useState({});
  const [loading, setLoading] = useState(false);
  const [assignClassOpen, setAssignClassOpen] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudent();
  }, [id]);

  const fetchStudent = async () => {
    setError("");
    setLoading(true);
    try {
      const response = await ApiClient.schools.getStudent({
        student: id,
      });
      if (response) {
        setPossibleClasses(response.possibleClassesForStudent);
        setStudent(response.student);
      }
    } catch (error) {
      console.error("Failed to fetch student", error);
      setError(String(error));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveClass = async (schoolClass) => {
    try {
      const response = await ApiClient.schools.removeStudentsFromClass({
        students: [student.id],
        schoolClass: schoolClass.id,
      });
      if (response.success) {
        fetchStudent();
      } else {
        console.error("Failed to remove student");
      }
    } catch (error) {
      console.error("Failed to remove student", error);
    }
  };

  if (loading) {
    return <GeneralLoading />;
  }

  if (!Object.keys(student).length) {
    return <Typography>Unable to load Student data. {error}</Typography>;
  }
  return (
    <FruitifyContainer>
      <FindClassAndAssignDialog
        open={assignClassOpen}
        setOpen={setAssignClassOpen}
        schoolClasses={possibleClasses}
        student={student}
        onSuccess={fetchStudent}
      />
      <StudentSummary student={student} onReload={() => fetchStudent()} />

      <Grid container spacing={4} direction={"row"} sx={{ mt: 2 }}>
        <Grid item size={{ xs: 12 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Classes
            </Typography>
            <Button
              variant="contained"
              onClick={() => setAssignClassOpen(true)}
            >
              Add Class
            </Button>
          </Box>
          <FruitifySchoolClassSearch
            possibleClasses={student.classes}
            onClassUnassignClicked={handleRemoveClass}
            onClassViewClicked={(schoolClass) =>
              navigate(`/management/classes/view/${schoolClass.id}`)
            }
          />
        </Grid>
      </Grid>
    </FruitifyContainer>
  );
}

export default StudentView;
